import { type FC, useMemo, useState, useEffect } from "react";
import {
  Radio,
  RadioGroup,
  Column,
  ToggleButtonGroup,
  ToggleButton,
  Text,
  Row,
  Box,
} from "@hightouch/htui";
import { useFlags } from "launchdarkly-react-client-sdk";
import { HightouchRegions, regionGroupConfigs } from "src/utils/regions";

type Props = {
  value: string;
  onChange: (value: string | undefined) => void;
  isDisabled?: boolean;
};

export const SelectRegion: FC<Readonly<Props>> = ({
  value,
  onChange,
  isDisabled,
}) => {
  const { multiRegionBetaRegions } = useFlags();
  const regions = useMemo(() => {
    return (
      Object.values(HightouchRegions)
        // Hidden regions (failover-only) are never selectable, regardless of flags
        .filter((config) => !config.hidden)
        // Filter out beta regions if the user is not flagged to see them
        .filter(
          (config) => !config.beta || (config.beta && multiRegionBetaRegions),
        )
    );
  }, [multiRegionBetaRegions]);

  const regionGroups = useMemo(() => {
    return Object.values(regionGroupConfigs).filter((regionGroup) =>
      Boolean(
        regions.some((region) => region.regionGroup.id === regionGroup.id),
      ),
    );
  }, [regions]);

  const [selectedGroup, setSelectedGroup] = useState<string | undefined>();

  // Get regions for the selected group
  const selectedGroupSupportedRegions = useMemo(() => {
    if (!selectedGroup) return [];
    return regions.filter((region) => region.regionGroup.id === selectedGroup);
  }, [selectedGroup, regions]);

  // Auto-select region if there's only one in the selected group
  useEffect(() => {
    if (selectedGroup && selectedGroupSupportedRegions.length === 1) {
      const singleRegion = selectedGroupSupportedRegions[0];
      if (singleRegion && value !== singleRegion.regionName) {
        onChange(singleRegion.regionName);
      }
    }
  }, [selectedGroup, selectedGroupSupportedRegions, value, onChange]);

  return (
    <Column gap={4}>
      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} width="100%">
        {regionGroups.map((group) => (
          <Box key={group.id} width="100%">
            <ToggleButtonGroup
              value={selectedGroup}
              onChange={(value: string) => {
                setSelectedGroup(value);
                onChange(undefined);
              }}
              variant="separated"
              size="lg"
              width="auto"
              isDisabled={isDisabled}
            >
              <ToggleButton
                value={group.id}
                label={group.label}
                accessory={{ type: "emoji", emoji: group.emoji }}
              />
            </ToggleButtonGroup>
          </Box>
        ))}
      </Box>

      {selectedGroup && selectedGroupSupportedRegions.length > 1 && (
        <RadioGroup onChange={onChange} value={value} orientation="vertical">
          {selectedGroupSupportedRegions.map((region) => (
            <Row key={region.regionName} align="center" gap={2}>
              <Radio
                label=""
                value={region.regionName}
                aria-labelledby={`region-${region.regionName}`}
              />
              <Box as={region.providerGroup.logo} />
              <Row gap={1}>
                <Text fontWeight="medium">{region.providerGroup.label}</Text>
                <Text id={`region-${region.regionName}`} color="text.tertiary">
                  {region.regionName}
                </Text>
              </Row>
            </Row>
          ))}
        </RadioGroup>
      )}
    </Column>
  );
};
